import React, { useState, useCallback, useRef, useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { navigate } from "gatsby-link";
import FormInput from "./elements/form-input";
import FormText from "./elements/form-text";
import FormSelect from "./elements/form-select";
import FormPerson from "./elements/form-person";
import FormPrivacy from "./elements/form-privacy";
import { validateEmail, validateTel } from "../utility/formatters";
import { getPrivacyData, submitData } from "../utility/forms";
import { GroupProvider } from "../context/group";
import AltchaWidget from "../utility/altcha";

const initialState = {
  type: "",
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  phone: "",
  mobilePhone: "",
  city: "",
  province: "",
  zipCode: "",
  description: "",
  orderNumber: "",
  reason: "",
};

const FormEcommerce = ({ data, labelManagerLabels }) => {
  const [formData, setFormData] = useState(() => ({
    ...initialState,
    type: data.tipology?.[0] === "Ecommerce Form" ? "" : data.tipology?.[0],
    ...getPrivacyData(data),
  }));

  const { formLabels, reasonLabels } = useStaticQuery(graphql`
    {
      formLabels: labelManagerLabels {
        formRefundReason
      }
      reasonLabels: labelManagerLabels {
        formNotWant
        formMistake
        formDamaged
        formNotWorking
        formMissingParts
        formWrongProduct
        formReasonOther
      }
    }
  `);

  const reasonOptions = useMemo(() => {
    const labels = Object.values(reasonLabels);
    return labels.every(Boolean)
      ? [
          {
            value: "",
            label: labelManagerLabels.select,
          },
        ].concat(
          labels.map((value) => ({
            value,
            label: value,
          }))
        )
      : [];
  }, [labelManagerLabels.select, reasonLabels]);

  //TODO: CHIAVI DI LINGUA
  const requestOpts = useMemo(
    () => [
      {
        value: "",
        label: labelManagerLabels.select,
      },
      {
        value: "Ecommerce product choice",
        label: labelManagerLabels.formChooseProduct,
      },
      {
        value: "Ecommerce purchase support",
        label: labelManagerLabels.formShopOnline,
      },
      {
        value: "Ecommerce order status",
        label: labelManagerLabels.formStateOrder,
      },
      {
        value: "Ecommerce info delivery",
        label: labelManagerLabels.formShipping,
      },
      {
        value: "Ecommerce return refund",
        label: labelManagerLabels.formReturnAndRefund,
      },
      {
        value: "Ecommerce account creation",
        label: labelManagerLabels.formCreateAccount,
      },
    ],
    [labelManagerLabels]
  );

  const currentRequest = data.tipology?.[0];
  const formtype = formData.type;
  const typeReturnAndRefund = formtype === "Ecommerce return refund";
  const matchIndice = requestOpts.findIndex((item) => item.value === formtype);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();

  const onChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    // console.log('onChange: ', value, checked, type)
    setFormData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;

      const formElements = [...form.elements].filter(
        (el) => el.tagName !== "BUTTON" && el.id !== "g-recaptcha-response"
      );
      const missingFields = formElements.filter(
        (field) =>
          field.getAttribute &&
          field.getAttribute("data-required") === "true" &&
          (!field.value || (field.type === "checkbox" && !field.checked))
      );

      const invalidFields = [];
      if (form.email?.value && !validateEmail(form.email.value)) {
        invalidFields.push("email");
      }
      if (form.phone?.value && !validateTel(form.phone.value)) {
        invalidFields.push("phone");
      }
      if (form.mobilePhone?.value && !validateTel(form.mobilePhone.value)) {
        invalidFields.push("mobilePhone");
      }
      if (missingFields.length || invalidFields.length) {
        const missingErrs = missingFields.reduce(
          (res, field) => ({
            ...res,
            [field.name]: labelManagerLabels.requiredFields,
          }),
          {}
        );
        const errors = invalidFields.reduce(
          (res, field) => ({ ...res, [field]: true }),
          missingErrs
        );
        setFormErrors((current) => ({ ...current, ...errors }));
        console.warn("Cannot submit: ", errors);
        const firstElError = formElements.find(
          (el) =>
            missingFields.some((field) => el.name === field.name) || invalidFields.includes(el.name)
        );
        var headerOffset =
          document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
        var elementPosition = firstElError.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          behavior: "smooth",
          top: offsetPosition,
        });

        return;
      }
      const submitPayload = { ...formDataRef.current };

      if (submitPayload.reason) {
        submitPayload.description = submitPayload.reason
          .concat("---")
          .concat(submitPayload.description);
        delete submitPayload.reason;
      }
      if (submitPayload.orderNumber) {
        submitPayload.description = submitPayload.orderNumber
          .concat("---")
          .concat(submitPayload.description);
        delete submitPayload.orderNumber;
      }

      setLoading(true);
      setServerError(false);

      try {
        const result = await submitData(
          { ...submitPayload },
          captcha,
          data.privacyText,
          "formEcommerce"
        );
        console.log("result: ", result);
        if (data.retUrl) {
          const slug = data.retUrl;
          navigate(slug.startsWith("/") ? slug : `/${slug}`);
        } else {
          navigate("/");
        }
      } catch (err) {
        setServerError(true);
        console.error("Error submitting: ", err);
        altchaRef.current?.reset();
        setCaptcha(null);
      }

      setLoading(false);
    },
    [data.privacyText, data.retUrl, labelManagerLabels, captcha]
  );

  return (
    <section className="section form">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="form__container">
              <form className="form" onSubmit={onSubmit}>
                <GroupProvider data={formInfo}>
                  <>
                    {currentRequest === "Ecommerce Form" && (
                      <FormSelect
                        id="request"
                        field="type"
                        label={labelManagerLabels.formRequest}
                        options={requestOpts}
                        isRequired={true}
                      />
                    )}
                    {![0].includes(matchIndice) && (
                      <>
                        {[2, 3, 4, 5].includes(matchIndice) ? (
                          <FormInput
                            id="orderNumber"
                            field="orderNumber"
                            maxLength={40}
                            placeholder={`${labelManagerLabels.formOrderNumber} ${
                              [3, 4, 5].includes(matchIndice) ? "*" : ""
                            }`}
                            isRequired={[3, 4, 5].includes(matchIndice) ? true : false}
                          />
                        ) : null}
                        <FormPerson
                          labelManagerLabels={labelManagerLabels}
                          mobileRequired={[4, 5].includes(matchIndice) ? true : false}
                          cityRequired={[3, 4, 5].includes(matchIndice) ? true : false}
                        />

                        {typeReturnAndRefund && !!reasonOptions.length && (
                          <FormSelect
                            id="reason"
                            field="reason"
                            label={formLabels.formRefundReason}
                            options={reasonOptions}
                            isRequired={true}
                          />
                        )}

                        <FormText
                          id="description"
                          field="description"
                          maxLength={32000}
                          placeholder={`${labelManagerLabels.formComments} *`}
                          isRequired={true}
                          rows={10}
                        />

                        <FormPrivacy
                          data={data.privacyText}
                          labelManagerLabels={labelManagerLabels}
                        />

                        <div className="my-5">
                          <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                        </div>
                        <div className="text-center">
                          <button
                            className="btn-link button primary"
                            disabled={!captcha || loading}
                          >
                            {labelManagerLabels.send}
                          </button>
                          {serverError && (
                            <p className="mt-2 form__service-not-available">
                              {labelManagerLabels.serviceNotAvailable}
                            </p>
                          )}
                        </div>

                        <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                      </>
                    )}
                  </>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FormEcommerce;
